// Las reglas de un VIDEO de referencia en una generación —
// ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente.
//
// Seedance 2 (calidad estándar) acepta un video de entrada de hasta 14 s y Seedance 2.5
// (profesional) de hasta 29 s: uno menos que la salida de cada uno, porque el proveedor
// lee el tope exacto como "más que el tope". De ahí las dos reglas del Estudio: un video
// de más de 14 s pasa la calidad a profesional solo; uno de más de 29 s no entra. Puro.

/** Hasta cuántos segundos de video de entrada acepta cada calidad. */
export const TOPE_ENTRADA_ESTANDAR_S = 14;
export const TOPE_ENTRADA_PROFESIONAL_S = 29;

export type VeredictoDeVideoDeReferencia =
  | {
      ok: true;
      /** La calidad que hace falta, o null si cualquiera sirve (o no se sabe la duración). */
      calidad: 'profesional' | null;
    }
  | { ok: false; motivo: string };

/** Qué hacer con un video de esa duración como referencia. `null` = duración desconocida. */
export function evaluarVideoDeReferencia(duracionSeg: number | null | undefined): VeredictoDeVideoDeReferencia {
  if (typeof duracionSeg !== 'number' || !Number.isFinite(duracionSeg) || duracionSeg <= 0) {
    return { ok: true, calidad: null };
  }
  if (duracionSeg > TOPE_ENTRADA_PROFESIONAL_S) {
    return {
      ok: false,
      motivo: `Ese video dura ${formatearSegundos(duracionSeg)} y como referencia entra hasta ${TOPE_ENTRADA_PROFESIONAL_S} s. Recortalo antes de usarlo.`,
    };
  }
  if (duracionSeg > TOPE_ENTRADA_ESTANDAR_S) return { ok: true, calidad: 'profesional' };
  return { ok: true, calidad: null };
}

/** ¿Este video necesita calidad profesional para entrar entero? */
export function pideProfesional(duracionSeg: number | null | undefined): boolean {
  const v = evaluarVideoDeReferencia(duracionSeg);
  return v.ok && v.calidad === 'profesional';
}

function formatearSegundos(s: number): string {
  const r = Math.round(s * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)} s`;
}

// ── Lo que Seedance acepta de un video de referencia ─────────────────────────────────────
// ADR-2026-10-20-el-video-de-referencia-se-ajusta-a-lo-que-acepta-seedance. Límites de la
// doc oficial (BytePlus ModelArk, "Create a video generation task"), iguales en Seedance 2
// y 2.5. La resolución y los fps los ajusta solos el backend al reencodear el tramo
// (`ajusteDeVideoDeReferencia` de atlas-backend): acá sólo se avisa. Lo que frena es lo que
// no tiene arreglo sin deformar o inventar: la proporción y menos de 2 s.

export const LIMITES_DE_SEEDANCE = {
  minPixeles: 407_696,
  /** ancho/alto: 0.4 es 2:5 y 2.5 es 5:2. Rango cerrado. */
  minProporcion: 0.4,
  maxProporcion: 2.5,
  minSegundos: 2,
} as const;

export interface AvisoDeVideo {
  /** true: con este video no se puede generar. false: se puede, pero conviene saberlo. */
  grave: boolean;
  texto: string;
}

/** Qué decirle al usuario sobre un video que quiere usar de referencia, o null si está bien. */
export function avisoDeVideoDeReferencia(v: { ancho?: number; alto?: number; duracionSeg?: number }): AvisoDeVideo | null {
  const L = LIMITES_DE_SEEDANCE;
  if (typeof v.duracionSeg === 'number' && v.duracionSeg > 0 && v.duracionSeg < L.minSegundos) {
    return {
      grave: true,
      texto: `Este video dura ${formatearSegundos(v.duracionSeg)}: para usarlo como referencia de un video tiene que durar al menos ${L.minSegundos} s.`,
    };
  }
  const ancho = v.ancho ?? 0;
  const alto = v.alto ?? 0;
  if (!(ancho > 0 && alto > 0)) return null;
  const medida = `${ancho}×${alto}`;
  const proporcion = ancho / alto;
  if (proporcion > L.maxProporcion || proporcion < L.minProporcion) {
    return {
      grave: true,
      texto: `Este video mide ${medida}: es demasiado ${proporcion > 1 ? 'apaisado' : 'angosto'} para usarlo como referencia de un video. El modelo acepta hasta 5:2 de ancho o de alto (por ejemplo 16:9, 1:1 o 9:16): recortalo y volvé a subirlo.`,
    };
  }
  if (ancho * alto < L.minPixeles) {
    return {
      grave: false,
      texto: `Este video es de baja resolución (${medida}). Para usarlo como referencia lo agrandamos solos, y el video que salga puede tener menos detalle.`,
    };
  }
  return null;
}

/**
 * Cuántos segundos del video de referencia se cobran: Enhancor factura la entrada además de
 * la salida, y Atlas los cobra a la misma tarifa
 * (ADR-2026-10-20-el-video-de-referencia-cobra-entrada-y-salida). Espejo de lo que viaja de
 * verdad (`recorteDeReferencia` del backend): el tramo dura lo que la salida, con el tope de
 * entrada de la calidad, nunca más que lo que queda del video (`tramoDisponible`) y como
 * mínimo 2 s. Redondeado para arriba, como la factura del proveedor.
 */
export function segundosDeEntradaACobrar(p: {
  duracionSalida: number;
  calidad: string | null | undefined;
  tramoDisponible?: number | null;
}): number {
  const tope = p.calidad === 'profesional' ? TOPE_ENTRADA_PROFESIONAL_S : TOPE_ENTRADA_ESTANDAR_S;
  let s = Math.min(Math.max(1, p.duracionSalida || 0), tope);
  const tramo = Number(p.tramoDisponible);
  if (Number.isFinite(tramo) && tramo > 0) s = Math.min(s, Math.max(LIMITES_DE_SEEDANCE.minSegundos, tramo));
  return Math.ceil(s);
}
